import { ReactElement, useMemo, useState } from 'react';
import { Info, Plus, SoftButton, Tooltip, Typography, useTray } from '@weareopenr/component-library';
import { SectionTrayContent } from 'src/components/SectionTray';
import { ManageAvailabilityTrayContent } from 'src/components/ManageAvailabilityTray';
import Divider from '../Divider';
import MenuSection, { ISectionTrayTarget } from '../MenuSection';
import { AddProductsToMenuTrayContent } from './AddProductsToMenuTray';
import MenuItemCardContainer from './MenuItemCardContainer';
import MenuItemSectionContainer from './MenuItemSectionContainer';
import { createCategoriesHierarchy, ICategoryWithChildren, isCategoryAssignedToMealtime } from './utilities';
import { IMenu } from 'src/types/menu.types';
import styles from './MenuSectionItems.module.scss';
import { CreateGlobalItemWizard } from '../CreateGlobalItemWizard/CreateGlobalItemWizard';
import { toMap } from 'src/helpers';
import { getNextOpenId } from 'src/helpers/getNextOpenId';

const MenuSectionItems = ({ menu }: { menu: IMenu }): ReactElement => {
  const [sectionId, setSectionId] = useState<string | null>(null);
  const [isGlobalItemWizardOpen, setIsGlobalItemWizardOpen] = useState<boolean>(false);
  const [pendingAddition, setPendingAddition] = useState<{ sectionId: string | null; count: number }>({
    sectionId: null,
    count: 0,
  });
  const [scrollToSectionId, setScrollToSectionId] = useState<string | null>(null);
  const { openTray, closeTray, isTrayOpen } = useTray();
  const { menuId, uncategorised, sections, modifiers, mealtimes } = menu;
  const categoriesHierachy = useMemo(() => createCategoriesHierarchy(sections), [sections, menu]);
  const sectionsMap = useMemo(() => toMap(sections, 'sectionId'), [sections]);

  const openSectionTray = (content: (openId: number) => ReactElement, title: string): void => {
    openTray(content(getNextOpenId()), { title });
  };

  const handleSectionModal = (options: ISectionTrayTarget = {}): void => {
    const { parentSectionId: targetParentSectionId = null, section } = options;
    const effectiveParentSectionId = section ? section.parentSectionId : targetParentSectionId;

    openSectionTray(
      (openId) => (
        <SectionTrayContent
          openId={openId}
          menuId={menuId}
          parentSectionId={effectiveParentSectionId}
          section={section}
          onClose={closeTray}
          onSaved={(savedSectionId, isNew) => {
            if (isNew) setScrollToSectionId(savedSectionId);
          }}
        />
      ),
      `${section ? 'Edit' : 'Create'} menu ${effectiveParentSectionId ? 'sub-section' : 'section'}`,
    );
  };

  const handleManageAvailabilityModal = (section: ICategoryWithChildren): void => {
    openSectionTray(
      (openId) => (
        <ManageAvailabilityTrayContent
          openId={openId}
          menuId={menuId}
          section={section}
          sections={sections}
          onClose={closeTray}
        />
      ),
      'Menu Section Availability',
    );
  };

  const showSelectProductsModal = (secId?: string): void => {
    const targetSectionId = secId && secId.length > 0 ? secId : null;
    setSectionId(targetSectionId);
    const sectionName = targetSectionId ? sectionsMap.get(targetSectionId)?.name : undefined;

    openSectionTray(
      (openId) => (
        <AddProductsToMenuTrayContent
          openId={openId}
          menuId={menuId}
          menuName={menu.name}
          sectionId={targetSectionId}
          menuRanges={menu.ranges}
          onCreateNewProduct={() => setIsGlobalItemWizardOpen(true)}
          onClose={closeTray}
          onSavingChange={(count) => setPendingAddition({ sectionId: count > 0 ? targetSectionId : null, count })}
        />
      ),
      sectionName ? `Add products to ${sectionName}` : 'Add products',
    );
  };

  const pendingCountFor = (targetSectionId: string | null) =>
    pendingAddition.sectionId === targetSectionId ? pendingAddition.count : 0;

  return (
    <>
      {menu && (
        <>
          <CreateGlobalItemWizard
            open={isGlobalItemWizardOpen}
            closeModal={() => setIsGlobalItemWizardOpen(false)}
            menuId={menuId}
            sectionId={sectionId}
          />
          <div className={styles['add-new-category']}>
            <SoftButton
              colour="blueberry"
              icon={Plus}
              size="full-width"
              disabled={isTrayOpen}
              onClick={() => handleSectionModal({ parentSectionId: null })}
            >
              Add menu section
            </SoftButton>
          </div>
          {categoriesHierachy.map((category: ICategoryWithChildren, index: number) => (
            <MenuItemSectionContainer
              key={category.sectionId}
              category={category}
              menuId={menuId}
              onClickNewItem={showSelectProductsModal}
              handleSectionModal={handleSectionModal}
              handleManageAvailabilityModal={handleManageAvailabilityModal}
              displayOrder={{
                up: categoriesHierachy[index - 1]?.displayOrder,
                down: categoriesHierachy[index + 1]?.displayOrder,
              }}
              isAssignedToMealtime={
                !mealtimes.length ? undefined : isCategoryAssignedToMealtime(category.sectionId, mealtimes)
              }
              tooltipPosition={index === 0 ? 'bottom' : 'top'}
              sections={sections}
              isAddProductsHidden={isTrayOpen}
              pendingAddition={pendingAddition}
              scrollToSectionId={scrollToSectionId}
              onScrolledIntoView={() => setScrollToSectionId(null)}
            />
          ))}
          {(uncategorised.length > 0 || sections.length === 0) && (
            <MenuSection
              variant="unassigned"
              menuId={menuId}
              section={{
                sectionId: '',
                name: 'Unassigned products',
                description:
                  'These products will not be published as part of your menu unless assigned to a menu section',
                displayOrder: 0,
                items: uncategorised,
                parentSectionId: null,
                children: [],
              }}
              onClickNewItem={showSelectProductsModal}
              isAssignedToMealtime={undefined}
              tooltipPosition="top"
              sections={sections}
              isAddProductsHidden={isTrayOpen}
              pendingAddition={pendingAddition}
            />
          )}
          {modifiers.length > 0 && (
            <>
              <Divider title="Modifiers">
                <Typography size="medium" weight="bold">
                  Modifiers
                </Typography>
                <div className={styles['info-tooltip-logo-wrapper']}>
                  <Tooltip text="Modifiers are saved and editable but won’t be published in the menu as separate products.">
                    <Info />
                  </Tooltip>
                </div>
              </Divider>
              {modifiers?.length > 0 && (
                <MenuItemCardContainer
                  items={modifiers}
                  menuId={menuId}
                  newItemButtonTitle="New modifier"
                  handleNewItemClick={() => showSelectProductsModal()}
                  isNewItemHidden={isTrayOpen}
                  pendingCount={pendingCountFor(null)}
                />
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export default MenuSectionItems;
